/// <amd-module name="Build/Designer/NewEditorView" />
import { IBuildLinkingService } from "Build/Client/Linking";
import { prefetch } from "Build/Common/Library/Components/CommandComponent";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import * as LibraryResources from "Build/Common/Library/Resources";
import { clearTreeStateOnUnmount } from "Build/Common/Library/StateHandler";
import { LegacyDesignerTabKeys } from "Build/Designer/Constants";
import { EditorAuthLandingPage } from "Build/Designer/EditorAuthLandingPage";
import { NewEditorHeader } from "Build/Designer/NewEditorHeader";
import { NewYamlTab } from "Build/Designer/NewYamlTab";
import { PageLoadScenarios, PerformanceConstants } from "Build/Designer/Performance";
import { IPipelineEditorBreadcrumbService } from "Build/Designer/PipelineEditorBreadcrumbService";
import * as Resources from "Build/Designer/Resources";
import { EditorError, IPipelineEditorViewService, getPipelineEditorViewService } from "Build/Designer/Services/PipelineEditorViewService";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IButtonProps } from "VSSUI/Button";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import * as React from "react";
import { IPipelineEditorActionsViewService, getPipelineEditorActionsViewService } from "./Services/PipelineEditorActionsViewService";

class NewEditorView extends VssComponent {
    private _ttiRecorded: boolean = false;
    private _editor: any; /*IEditor*/
    private actionsService: IPipelineEditorActionsViewService;
    private viewService: IPipelineEditorViewService;
    private isValidateAndSavePipelineByDefaultEnabled: boolean;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.viewService = getPipelineEditorViewService(context.pageContext);
        this.actionsService = getPipelineEditorActionsViewService(context.pageContext);
        this.isValidateAndSavePipelineByDefaultEnabled = isFeatureFlagEnabled(context.pageContext, "Build2.ValidateAndSavePipelineByDefault", false);

    }

    public componentDidMount(): void {
        super.componentDidMount();
        prefetch(this.context.pageContext);
        this.breadcrumbService.setPipeline(this.viewService.getDefinition());

        // If login is required, redirect after the initial render so we get an Authorizing spinner
        const authentication = this.viewService.getAuthentication();
        if (authentication.isLoginRequired && !authentication.shouldShowLandingPage && authentication.loginUrl) {
            openLinkSecurely(authentication.loginUrl);
        } else {
            this.actionsService.checkForAppConnectionUpgrade();

            // preload the schema here so we don't call the old one endpoint without template data
            this.actionsService.validateAndUpdateSchema(false);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.breadcrumbService.clearPipeline();
        clearTreeStateOnUnmount(this.context.pageContext);
    }

    public render(): JSX.Element {
        // If the definition is not found, return a minimal header with the error message
        if (!this.viewService.getDefinition()) {
            return this._getEmptyPage();
        }

        // If the yaml is protected, require login before showing it (show landing page or redirect after render)
        if (this.viewService.shouldShowLandingPage()) {
            return this._getAuthLandingPage();
        }

        // Otherwise, show the editor page as expected
        return this._getEditorPage();
    }

    private _getEmptyPage(): JSX.Element {
        return <Page className={pageClassName}>{this._getHeader()}</Page>;
    }

    private _getAuthLandingPage(): JSX.Element {
        return (
            <Page className={pageClassName}>
                {this._getHeader()}
                <EditorAuthLandingPage
                    loginUrl={this.viewService.getAuthentication().loginUrl}
                    yamlFileLink={this.viewService.getYamlFileUrl()}
                    repository={this.viewService.getRepository()}
                    sourceProvider={this.viewService.getSourceProvider()}
                />
            </Page>
        );
    }

    private _getEditorPage() {
        return (
            <Page className={pageClassName}>
                {this._getHeader()}
                <Observer content={this.viewService.getYamlContent()}>
                    {({ content }: { content: string }) => (
                        <NewYamlTab
                            content={content}
                            editor={this._editor}
                            onContentUpdated={newContent => this.viewService.setYamlContent(newContent)}
                            onEditorCreated={this._onEditorCreated}
                            onEditorDisposing={this._onEditorDisposing}
                            sidePanelInitiallyOpen={true}
                            codeLensOptions={
                                this.actionsService.canValidate()
                                    ? {
                                          onOpenTemplate: this.actionsService.openTemplate,
                                          onValidate: () => this.actionsService.validateAndUpdateSchema(true),
                                          checkTemplate: this.actionsService.checkTemplate
                                      }
                                    : {}
                            }
                        />
                    )}
                </Observer>
            </Page>
        );
    }

    private _getHeader(): JSX.Element {
        return (
            <>
                <Observer
                    error={this.viewService.getEditorError()}
                    isRunning={this.actionsService.getIsRunning()}
                    isSaving={this.actionsService.getIsSaving()}
                    isYamlDirty={this.viewService.getIsYamlDirty()}
                >
                    {({
                        error,
                        isRunning,
                        isSaving,
                        isYamlDirty
                    }: {
                        error: EditorError;
                        isRunning: boolean;
                        isSaving: boolean;
                        isYamlDirty: boolean;
                    }) => (
                        <NewEditorHeader
                            definition={this.viewService.getDefinition()}
                            isYamlDirty={isYamlDirty}
                            actionButtonLabel={this.viewService.getIsTemplate()
                                ? Resources.SaveButtonLabel
                                : isYamlDirty
                                ?  this.isValidateAndSavePipelineByDefaultEnabled
                                    ? Resources.ValidateAndSaveButtonLabel
                                    : Resources.SaveButtonLabel
                                : Resources.RunButtonLabel}
                            actionDisabled={!!error || isRunning || isSaving}
                            onActionClick={
                                this.viewService.getIsTemplate()
                                    ? () => this.actionsService.showSavePanel()
                                    : isYamlDirty
                                    ?  this.isValidateAndSavePipelineByDefaultEnabled
                                      ? () => this.actionsService.showValidateAndSavePanel()
                                      : () => this.actionsService.showSavePanel()
                                    : () => this.actionsService.showQueuePanel()
                            }
                            onSaveWithoutValidationClick={() => this.actionsService.showSavePanel()}
                            onFullYaml={() => this.actionsService.downloadYaml()}
                            onRunWithParameters={() => this.actionsService.runBuildWithParameters()}
                            onSettingsOpen={() => this.actionsService.showSettingsPanel()}
                            onValidateYaml={() => this.actionsService.validateAndUpdateSchema(true)}
                            onVariablesOpen={() => this.actionsService.showVariablesPanel()}
                            buildMessageInfo={this.actionsService.getBuildMessage()}
                            onBuildMessageDismissed={() => this.actionsService.dismissBuildMessage()}
                            errorMessage={error?.message}
                            errorActions={error && this.getErrorActions(error)}
                            onErrorMessageDismissed={error?.isDismissable ? () => this.viewService.clearEditorError() : undefined}
                            title={this.viewService.getDefinition()?.name || Resources.EditorDefaultTitle}
                        />
                    )}
                </Observer>
                <Observer canSwitch={this.actionsService.canSwitchToAppConnection()} canInstall={this.actionsService.canInstallApp()}>
                    {() => {
                        const actions: IButtonProps[] = [];
                        let message: string;
                        if (this.actionsService.canSwitchToAppConnection().value) {
                            message = LibraryResources.PreferAppConnectionChange;
                            actions.push({
                                text: LibraryResources.ChangeToAppConnection,
                                onClick: () => this.actionsService.switchToAppConnection()
                            });
                        } else if (this.actionsService.canInstallApp().value) {
                            message = LibraryResources.PreferAppConnectionInstall;
                            actions.push({
                                text: LibraryResources.InstallApp,
                                onClick: () => this.actionsService.installApp()
                            });
                        } else {
                            return null;
                        }

                        const connection = this.viewService.getConnection();
                        return (
                            <div className="page-content flex-row flex-noshrink">
                                <MessageCard
                                    className="flex-grow"
                                    severity={MessageCardSeverity.Info}
                                    onDismiss={() => this.actionsService.dismissAppConnectionRecommendations()}
                                    buttonProps={actions}
                                >
                                    <FormatComponent format={message}>
                                        <FPSLink href={connection.appHelpLink}>{connection.appName}</FPSLink>
                                    </FormatComponent>
                                </MessageCard>
                            </div>
                        );
                    }}
                </Observer>
            </>
        );
    }

    private getErrorActions(error: EditorError): IButtonProps[] {
        if (error.proposeClassicDesigner) {
            const pipelineId = Number(this.historyService.getState().pipelineId);
            const definitionEditUrl = this.buildLinkingService.getDefinitionEditUrl(pipelineId, LegacyDesignerTabKeys.Yaml);
            return [
                {
                    text: Resources.EditInOldDesigner,
                    href: definitionEditUrl
                }
            ];
        }

        if (error.proposeAuthorizeAndRun) {
            return [
                {
                    primary: true,
                    text: Resources.AuthorizeAndRunButtonText,
                    onClick: () => {
                        this.actionsService.authorizeAndRun();
                    }
                }
            ];
        }

        return [];
    }

    private _onEditorCreated = (editor: any /*IEditor*/): void => {
        if (editor) {
            this.actionsService.setEditor(editor);
            editor.focus();

            this._editor = editor;
            this.forceUpdate();

            // if the YAML editor tab was the initial tab (and it probably was) TTI is when the editor loads
            this._markTimeToInteractive();
        }
    };

    private _onEditorDisposing = (): void => {
        this.actionsService.setEditor(undefined);
    };

    private _markTimeToInteractive = (): void => {
        if (!this._ttiRecorded) {
            this._ttiRecorded = true;
            this.context.pageContext
                .getService<IVssPerformanceService>("IVssPerformanceService")
                .markTimeToInteractive(PerformanceConstants.Area, PageLoadScenarios.YamlEditor);
        }
    };

    private get breadcrumbService(): IPipelineEditorBreadcrumbService {
        return this.context.pageContext.getService<IPipelineEditorBreadcrumbService>("pipeline-editor-breadcrumb-service");
    }

    private get buildLinkingService(): IBuildLinkingService {
        return this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
    }

    private get historyService(): IVssHistoryService {
        return this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }
}

const pageClassName = "pipeline-editor-view bolt-page-grey flex-column flex-grow";

Components.add("new-ci-designer-editor-view", NewEditorView);
